import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import StatsCard from '../Common/StatsCard';
import DataTable from '../Common/DataTable';
import BarChart from '../Common/BarChart';
import DonutChart from '../Common/DonutChart';
import DashboardHero from '../Common/DashboardHero';

export default function TeacherDashboard() {
  const [myClass, setMyClass] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchClass(); }, []);

  const fetchClass = async () => {
    setLoading(true);
    try {
      const res = await api.get('/teacher/my-class');
      setMyClass(res.data);
    } catch (err) {
      console.error('Failed to load class:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="bg-white p-8 rounded shadow text-center text-gray-500">Loading...</div>;
  }

  if (!myClass || !myClass.class_name) {
    return (
      <div className="bg-white p-8 rounded shadow text-center">
        <i className="fas fa-chalkboard text-4xl text-gray-300 mb-3"></i>
        <p className="text-gray-500">You are not assigned to any class yet. Contact your principal.</p>
      </div>
    );
  }

  const students = myClass.students || [];
  const totalStudents = myClass.total_students;
  const avgAttendance = myClass.average_attendance;
  const avgGrade = myClass.average_grade;
  const examCount = myClass.exam_count;

  const attendanceChartData = students.map(s => ({
    label: s.first_name.substring(0, 6),
    value: s.attendance_percent,
    color: s.attendance_percent >= 90 ? '#10b981'
      : s.attendance_percent >= 75 ? '#f59e0b'
      : '#ef4444',
  }));

  const gradeChartData = students.map(s => ({
    label: s.first_name.substring(0, 6),
    value: s.average_percent,
    color: s.average_percent >= 80 ? '#10b981'
      : s.average_percent >= 60 ? '#3b82f6'
      : '#f59e0b',
  }));

  const paidCount = students.filter(s => s.fees_paid).length;
  const pendingCount = totalStudents - paidCount;
  const feeDonutData = [
    { label: 'Fees Paid', value: paidCount, color: '#10b981' },
    { label: 'Pending', value: pendingCount, color: '#ef4444' },
  ].filter(d => d.value > 0);

  const columns = ['Roll #', 'Name', 'Attendance', 'Avg %', 'Grade', 'Fee Status'];
  const data = students.map(s => ({
    'Roll #': s.roll_number || '—',
    Name: s.name,
    Attendance: (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${
        s.attendance_percent >= 90 ? 'bg-green-100 text-green-800'
        : s.attendance_percent >= 75 ? 'bg-yellow-100 text-yellow-800'
        : 'bg-red-100 text-red-800'
      }`}>{s.attendance_percent}%</span>
    ),
    'Avg %': s.average_percent ? `${s.average_percent}%` : '—',
    Grade: (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${
        s.letter_grade?.startsWith('A') ? 'bg-green-100 text-green-800'
        : s.letter_grade?.startsWith('B') ? 'bg-blue-100 text-blue-800'
        : s.letter_grade?.startsWith('C') ? 'bg-yellow-100 text-yellow-800'
        : 'bg-gray-100 text-gray-800'
      }`}>{s.letter_grade}</span>
    ),
    'Fee Status': (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${
        s.fees_paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>
        {s.fees_paid ? 'Paid' : `$${s.pending_fees.toFixed(0)}`}
      </span>
    ),
  }));

  return (
    <div>
      <DashboardHero
        color="emerald"
        icon="fa-chalkboard-teacher"
        title={`Class ${myClass.class_name}`}
        subtitle={`${myClass.campus_name} · ${myClass.academic_year || 'N/A'} · ${totalStudents} students`}
      >
        <button
          onClick={fetchClass}
          className="bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-lg text-sm font-medium transition"
        >
          <i className="fas fa-sync-alt mr-1"></i> Refresh
        </button>
      </DashboardHero>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatsCard label="Students" value={totalStudents} icon="fa-user" />
        <StatsCard label="Exams Held" value={examCount} icon="fa-pencil-alt" />
        <StatsCard label="Avg Attendance" value={`${avgAttendance}%`} icon="fa-percent" />
        <StatsCard label="Avg Grade" value={`${avgGrade}%`} icon="fa-star" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <BarChart title="📊 Attendance per Student" data={attendanceChartData} height={320} suffix="%" />
        <BarChart title="📈 Average Grade per Student" data={gradeChartData} height={320} color="#10b981" suffix="%" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <DonutChart title="💰 Fee Status of Class" data={feeDonutData} size={260} />
        <div className="bg-white p-5 rounded shadow">
          <h3 className="font-semibold text-lg text-gray-800 mb-4">📋 Class Summary</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
              <span className="text-blue-700 font-medium">Total Students</span>
              <span className="text-2xl font-bold text-blue-900">{totalStudents}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-green-50 rounded">
              <span className="text-green-700 font-medium">Average Grade</span>
              <span className="text-2xl font-bold text-green-900">{avgGrade}%</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-yellow-50 rounded">
              <span className="text-yellow-700 font-medium">Average Attendance</span>
              <span className="text-2xl font-bold text-yellow-900">{avgAttendance}%</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-purple-50 rounded">
              <span className="text-purple-700 font-medium">Fees Paid</span>
              <span className="text-2xl font-bold text-purple-900">{paidCount}/{totalStudents}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white p-4 rounded shadow">
        <h2 className="text-lg font-semibold mb-3">👥 Class {myClass.class_name} Students</h2>
        {data.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <i className="fas fa-inbox text-4xl mb-3"></i>
            <p>No students assigned to your class yet.</p>
          </div>
        ) : (
          <DataTable columns={columns} data={data} />
        )}
      </div>
    </div>
  );
}